import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Upload, Image as ImageIcon } from 'lucide-react';
import { getCompanyLogoUrl } from '../../utils/imageUtils';
import { useCompanies } from '../../hooks/useCompanies';

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1.5">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const inputCls = (error) =>
  `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-400 bg-white ${
    error ? 'border-red-300' : 'border-gray-200'
  }`;

const CompanyForm = ({ company, onClose }) => {
  const { createCompany, updateCompany, loading } = useCompanies();
  const isEditing = !!company;

  const [formData, setFormData] = useState({
    nombre: '', nombre_comercial: '', rnc: '', ruc: '',
    email: '', telefono: '', direccion: '', ciudad: '',
    pais: 'República Dominicana', estado: 'activa',
  });
  const [logoFile, setLogoFile]     = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors]         = useState({});

  useEffect(() => {
    if (company) {
      setFormData({
        nombre:           company.nombre           || '',
        nombre_comercial: company.nombre_comercial || '',
        rnc:              company.rnc              || '',
        ruc:              company.ruc              || '',
        email:            company.email            || '',
        telefono:         company.telefono         || '',
        direccion:        company.direccion        || '',
        ciudad:           company.ciudad           || '',
        pais:             company.pais             || 'República Dominicana',
        estado:           company.estado           || 'activa',
      });
      if (company.logo) setLogoPreview(getCompanyLogoUrl(company.logo));
    }
  }, [company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Máximo 2MB.'); return; }
    if (!file.type.startsWith('image/')) { alert('Solo imágenes.'); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    if (!formData.nombre.trim()) e.nombre = 'El nombre es requerido';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      e.email = 'Email inválido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    const data = { ...formData };
    if (logoFile) data.logo = logoFile;
    const result = isEditing
      ? await updateCompany(company.id, data)
      : await createCompany(data);
    if (result.success) {
      onClose(true);
    } else {
      alert(result.error);
    }
  };

  return (
    <div className="space-y-4">

      {/* ── Back button ─────────────────────────────────────────── */}
      <button
        onClick={() => onClose(false)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft size={16} />
        Volver a Compañías
      </button>

      {/* ── Title card ──────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl px-6 py-4">
        <h2 className="text-lg font-bold text-gray-900">
          {isEditing ? 'Editar Compañía' : 'Nueva Compañía'}
        </h2>
        {isEditing && (
          <p className="text-sm text-gray-400 mt-0.5">{company.nombre}</p>
        )}
      </div>

      {/* ── Form ────────────────────────────────────────────────── */}
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Logo */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Logo</p>
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center shrink-0 overflow-hidden">
              {logoPreview
                ? <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                : <ImageIcon size={24} className="text-gray-300" />
              }
            </div>
            <div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
                <Upload size={14} />
                {logoPreview ? 'Cambiar logo' : 'Subir logo'}
                <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
              </label>
              <p className="text-xs text-gray-400 mt-1.5">PNG, JPG — máx. 2MB</p>
            </div>
          </div>
        </div>

        {/* Información general */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Información general</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="Nombre legal" required error={errors.nombre}>
                <input name="nombre" value={formData.nombre} onChange={handleChange}
                  className={inputCls(errors.nombre)} placeholder="Nombre legal de la compañía" />
              </Field>
            </div>

            <div className="md:col-span-2">
              <Field label="Nombre comercial">
                <input name="nombre_comercial" value={formData.nombre_comercial} onChange={handleChange}
                  className={inputCls()} placeholder="Nombre comercial (opcional)" />
              </Field>
            </div>

            <Field label="RNC">
              <input name="rnc" value={formData.rnc} onChange={handleChange}
                className={inputCls()} placeholder="000-00000-0" />
            </Field>

            <Field label="RUC">
              <input name="ruc" value={formData.ruc} onChange={handleChange}
                className={inputCls()} placeholder="RUC (opcional)" />
            </Field>

            {isEditing && (
              <Field label="Estado">
                <select name="estado" value={formData.estado} onChange={handleChange} className={inputCls()}>
                  <option value="activa">Activa</option>
                  <option value="inactiva">Inactiva</option>
                </select>
              </Field>
            )}
          </div>
        </div>

        {/* Contacto */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Contacto</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Email" error={errors.email}>
              <input type="email" name="email" value={formData.email} onChange={handleChange}
                className={inputCls(errors.email)} placeholder="email@ejemplo.com" />
            </Field>

            <Field label="Teléfono">
              <input type="tel" name="telefono" value={formData.telefono} onChange={handleChange}
                className={inputCls()} placeholder="(809) 000-0000" />
            </Field>

            <div className="md:col-span-2">
              <Field label="Dirección">
                <textarea name="direccion" value={formData.direccion} onChange={handleChange}
                  rows={2} className={inputCls()} placeholder="Dirección completa" />
              </Field>
            </div>

            <Field label="Ciudad">
              <input name="ciudad" value={formData.ciudad} onChange={handleChange}
                className={inputCls()} placeholder="Santo Domingo" />
            </Field>

            <Field label="País">
              <input name="pais" value={formData.pais} onChange={handleChange} className={inputCls()} />
            </Field>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => onClose(false)}
            disabled={loading}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading
              ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Guardando...</>
              : <><Save size={14} />{isEditing ? 'Guardar cambios' : 'Crear compañía'}</>
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyForm;
